if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        // Get all known variant SKUs (cached after first call)
        const allVariantSkus = this.getAllVariantSkus();
        
        // Get the currently selected variant SKU, fallback to the image's alt text if null
        const currentVariantSku = this.getCurrentVariantSku() || (activeMedia.querySelector('img')?.alt.trim() ?? null);

        // Filter main viewer slides.
        // Rule: hide ONLY images whose alt matches a DIFFERENT variant's SKU.
        // Images whose alt doesn't match any SKU are "shared" and always shown.
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          const img = element.querySelector('img');
          const imgAlt = img ? img.alt.trim() : null;

          const isOtherVariantImage =
            imgAlt && allVariantSkus.size > 0 && allVariantSkus.has(imgAlt) && imgAlt !== currentVariantSku;

          element.style.display = isOtherVariantImage ? 'none' : '';
          element.classList.remove('is-active');
        });

        // Filter thumbnails with the same rule
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
            const img = thumbnail.querySelector('img');
            const imgAlt = img ? img.alt.trim() : null;

            const isOtherVariantImage =
              imgAlt && allVariantSkus.size > 0 && allVariantSkus.has(imgAlt) && imgAlt !== currentVariantSku;

            thumbnail.style.display = isOtherVariantImage ? 'none' : '';
          });
          if (this.elements.thumbnails.slider) {
            this.elements.thumbnails.resetPages();
          }
        }

        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }

        // Call resetPages on viewer slider to recalculate width/items
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      /**
       * Returns the SKU of the currently selected variant.
       * Reads from the <script data-selected-variant> tag in the DOM.
       */
      getCurrentVariantSku() {
        const scriptTag = document.querySelector('variant-selects script[data-selected-variant]');
        if (scriptTag) {
          try {
            const variant = JSON.parse(scriptTag.textContent);
            if (variant && variant.sku) return variant.sku.trim();
          } catch (e) {
            console.error('media-gallery: failed to parse selected variant', e);
          }
        }
        return null;
      }

      /**
       * Returns a Set of all variant SKUs for this product.
       * Reads from the <script data-all-variant-skus> tag injected by product-variant-picker.liquid.
       * Result is cached on the instance after the first call.
       */
      getAllVariantSkus() {
        if (this._allVariantSkus) return this._allVariantSkus;

        const scriptTag = document.querySelector('variant-selects script[data-all-variant-skus]');
        if (!scriptTag) {
          this._allVariantSkus = new Set();
          return this._allVariantSkus;
        }

        try {
          const skus = JSON.parse(scriptTag.textContent);
          // Build a Set of non-empty, trimmed SKUs for fast O(1) lookup
          this._allVariantSkus = new Set(skus.filter(Boolean).map((s) => s.trim()));
        } catch (e) {
          console.error('media-gallery: failed to parse variant SKUs', e);
          this._allVariantSkus = new Set();
        }

        return this._allVariantSkus;
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
